import { UTCDate } from "@date-fns/utc";
import { eachMonthOfInterval, eachWeekOfInterval, endOfDay, endOfMonth, endOfWeek, format, startOfDay } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import CollectionUtils from "../../../../../utils/collections";
import AcrivityLogRepo from "../../../../../utils/repository/activityLogRepo";
import TableUpdatesRepo from "../../../../../utils/repository/tableUpdatesRepo";
import { getTableModel } from "../../../activityLogs/utils";
import { adjustedUserGroupedMistakeKPIs, getRawUserGroupedMistakeKPIs } from "../../utils/qualityUtils";
import { ConfigProps, DEFAULT_CONFIG } from "../productivity/constants";
import { getQuantityPerformance, getTableData, getTableTypes } from "../productivity/utils";
import { kpisIgnoredUsers, INTERVAL_TYPE, StatisticProps } from "./constants";
import CloneUtils from "../../../../../utils/cloneUtils";
import { getLinkedUserIds, getDeclaredWorkForUser, getTotalDeclaredTaskMinutes } from "./userDeclaredWorkUtils";


class Cache {

    constructor() {
        this._cachedData = {}
    }

    /**
     * @param {any} dataProviderArgs arguments to be passed to the data provided to retrieve the data when needed. Required
     * @param {function} dataProvider Data provider data accepts the "dataProviderArgs" and a data consumer. Required
     * @param {function} dataConsumer Data consumer used by the client to retrieve the requested data.
     * 
     * NOTE: The properties (if any) of the dataProviderArgs must be ordered consistently in to allow the cache to work properly.
     */
    get(dataProviderArgs, dataProvider, dataConsumer) {

        const keyStr = JSON.stringify(dataProviderArgs) + "__" + dataProvider

        if (this._cachedData[keyStr] !== undefined) {
            dataConsumer(CloneUtils.deepClone(this._cachedData[keyStr]))
        } else {
            dataProvider(dataProviderArgs, data => {
                this._cachedData[keyStr] = data
                dataConsumer(CloneUtils.deepClone(data))
            })
        }
    }
}

const TASKS_EXECUTOR_CACHE = new Cache()

export function getIntervals(startDateUTC, endDateUTC, intervalType) {
    let intervals = []
    if (intervalType === INTERVAL_TYPE.MONTHLY) {
        intervals = getMonthlyIntervals(startDateUTC, endDateUTC)
    } else if (intervalType === INTERVAL_TYPE.WEEKLY) {

        intervals = getWeeklyIntervals(startDateUTC, endDateUTC);
    }
    return intervals
}

function getWeeklyIntervals(startDate, endDate) {
    // Using eachWeekOfInterval to get the start of each week
    const weeklyIntervals = [];
    const startOfEachWeek = eachWeekOfInterval(
        { start: startDate, end: endDate },
    );

    // For each start date, create a weekly interval
    startOfEachWeek.forEach((weekStart) => {
        // The end date is 6 days after the start
        const weekEnd = endOfWeek(weekStart);

        weeklyIntervals.push({
            startDate: startOfDay(weekStart).getTime(),
            endDate: endOfDay(weekEnd).getTime(),
        });
    });

    return weeklyIntervals;
}

function getMonthlyIntervals(startDate, endDate) {
    const monthlyIntervals = [];
    const startOfEachMonth = eachMonthOfInterval({ start: startDate, end: endDate });

    startOfEachMonth.forEach(monthStart => {
        const monthEnd = endOfMonth(monthStart);
        monthlyIntervals.push({
            startDate: startOfDay(monthStart).getTime(),
            endDate: endOfDay(monthEnd).getTime()
        })
    })

    return monthlyIntervals
}

function sequentialTasksExecutor(executor, params, singleTaskProcessor, resultProcessor) {
    let cancelled = false;
    let currentParamIndex = 0;
    const tasksResults = []

    function makeApiCall() {
        if (cancelled) {
            return;
        }
        if (currentParamIndex < params.length) {
            const param = params[currentParamIndex]
            TASKS_EXECUTOR_CACHE.get(param, executor, (data) => {
                if (cancelled) {
                    return;
                }
                tasksResults.push(singleTaskProcessor(data, param))
                currentParamIndex += 1;
                makeApiCall()
            })
        } else {
            resultProcessor(tasksResults)
        }
    }

    makeApiCall()
    return () => { cancelled = true; };
}

export function useProductivity(usersMap, intervals) {
    const [result, setResult] = useState({ intervals: null, performanceArray: [] });
    const { performanceArray } = result;
    const [allTableTypes, setAllTableTypes] = useState([]);
    const [config, setConfig] = useState({ ...DEFAULT_CONFIG });

    useEffect(() => {
        let tableTypes = [];
        const params = intervals.map(interval => ({ from: interval.startDate, to: interval.endDate, includeDataWithUnknownMistakes: true, }))
        return sequentialTasksExecutor(TableUpdatesRepo.performanceKpisV2, params, (performance, param) => {
            tableTypes = Array.from(new Set([...tableTypes, ...getTableTypes(performance)]));
            return {
                endDate: param.to,
                performance,
            }
        }, (performanceArray) => {
            setConfig((prev) => ({ ...prev, [ConfigProps.TABLE_TYPES]: tableTypes }));
            setAllTableTypes(tableTypes);
            setResult({ intervals, performanceArray });
        })
    }, [intervals]);

    const productivityData = useMemo(() => {
        return performanceArray.map(({ performance, endDate }) => {
            const userToMistakesKPIs = getRawUserGroupedMistakeKPIs(
                Object.keys(usersMap),
                performance,
                config[ConfigProps.TABLE_TYPES]
            );

            const mistakeAjustedKPIs = CollectionUtils.arrToMap(
                adjustedUserGroupedMistakeKPIs(userToMistakesKPIs),
                (userKips) => userKips.userId
            );

            const tableData = getTableData(getQuantityPerformance(performance, usersMap, config, mistakeAjustedKPIs));

            return {
                endDate,
                total: tableData.find((x) => x.userId === 'TOTAL'),
            };
        }).reduce((acc, curr) => {
            acc[curr.endDate] = curr;
            return acc
        }, {});
    }, [performanceArray, usersMap, config]);

    return [productivityData, result.intervals !== intervals, allTableTypes, config, setConfig]
}

export function useActivity(intervals, selectedUserId = null) {
    const [result, setResult] = useState({ intervals: null, activityLogsArray: [] });

    useEffect(() => {
        const params = intervals.map(interval => ({ from: interval.startDate, to: interval.endDate }));
        return sequentialTasksExecutor(AcrivityLogRepo.list, params, (activityLogs, param) => {
            return {
                endDate: param.to,
                logs: activityLogs,
            };
        }, (activityLogsArray) => {
            setResult({ intervals, activityLogsArray });
        })
    }, [intervals]);

    const activity = useMemo(
        () => getActivityData(result.activityLogsArray, selectedUserId),
        [result.activityLogsArray, selectedUserId]
    );
    return [activity, result.intervals !== intervals];
}

function getActivityData(activityLogsArray, selectedUserId = null) {
    const selectedUserIds = selectedUserId === null ? null : new Set(getLinkedUserIds(selectedUserId));
    return activityLogsArray.reduce((acc, { logs, endDate }) => {
        const filteredLogs = logs.filter(log => {
            const userId = Number(log.user.id);
            return !kpisIgnoredUsers.includes(userId)
                && (selectedUserIds === null || selectedUserIds.has(userId));
        });
        // getTableModel merges sessions in place; retain the original logs for switching users.
        const sortedLogs = CloneUtils.deepClone(filteredLogs)
            .map(log => ({ ...log, user: { ...log.user, id: Number(log.user.id) } }))
            .sort((a, b) => new Date(b.start) - new Date(a.start));
        const { middleDuration, totalClicks, totalKeystrokes } = getTableModel(sortedLogs);
        acc[endDate] = {
            endDate,
            total: { totalClicks, totalKeystrokes, actualWorkingHours: middleDuration / 60 },
        };
        return acc;
    }, {});
}

export function createStatisticsArray(productivity, activity, selectedUserId = null, intervalType = INTERVAL_TYPE.MONTHLY) {
    return Object.values(productivity).map(({ endDate }) => {
        const totalScore = productivity[endDate].total[StatisticProps.totalScore.propName]
        const qaScore = productivity[endDate].total[StatisticProps.visualChecks.propName]

        /** middle duration is actual working minutes */
        const actualWorkingHours = activity[endDate]?.total[StatisticProps.actualWorkingHours.propName]
        const totalScoreToActualWorkingHoursRatio = calcRatio(totalScore, actualWorkingHours);

        const declaredWorkingHours = getIntervalDeclaredWorkingHours(endDate, selectedUserId, intervalType);
        let taskHours = declaredWorkingHours?.taskHours ?? null;
        if (selectedUserId === null && intervalType === INTERVAL_TYPE.MONTHLY) {
            taskHours = minutesToHours(getTotalDeclaredTaskMinutes(endDate));
        }
        const actualToDeclaredWorkingHoursRatio = calcRatio(actualWorkingHours, declaredWorkingHours?.total);
        const totalScoreToTotalDeclaredWorkingHoursRatio = calcRatio(totalScore, declaredWorkingHours?.total);
        const qaScoreToDeclaredQaWorkingHoursRatio = calcRatio(qaScore, declaredWorkingHours?.qa);
        const fillerScore = Number.isFinite(totalScore) && Number.isFinite(qaScore) ? totalScore - qaScore : null;
        const fillerScoreToDeclaredFillerWorkingHoursRatio = calcRatio(fillerScore, declaredWorkingHours?.filler);

        return {
            endDate,
            total: {
                ...productivity[endDate].total,
                ...activity[endDate]?.total,
                totalScoreToActualWorkingHoursRatio,
                declaredWorkingHours: declaredWorkingHours?.total ?? null,
                calendarHours: declaredWorkingHours?.calendarHours ?? null,
                taskHours,
                qaHours: declaredWorkingHours?.qa ?? null,
                fillerHours: declaredWorkingHours?.filler ?? null,
                spreadsheetCellScore: declaredWorkingHours?.spreadsheetCellScore ?? null,
                actualToDeclaredWorkingHoursRatio,
                totalScoreToTotalDeclaredWorkingHoursRatio,
                qaScoreToDeclaredQaWorkingHoursRatio,
                fillerScoreToDeclaredFillerWorkingHoursRatio
            }
        }
    })
}

/**
 * 
 * @param {number} numerator required
 * @param {number} denominator  optional
 */
function calcRatio(numerator, denominator) {
    if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
        return null;
    }
    const ratio = numerator / denominator;
    return Number.isFinite(ratio) ? ratio : null;
}

export function getChartData(statisticArray, propsToPlotOnChart) {
    const labelsRow = ['Time', ...propsToPlotOnChart.map(p => StatisticProps[p].label)];

    const chartData = [labelsRow];

    statisticArray.forEach((data) => {
        const row = [format(new UTCDate(data.endDate), "MMM do, yyyy")];

        propsToPlotOnChart.forEach((prop) => {
            row.push(Number.isFinite(data.total[prop]) ? data.total[prop] : null);
        });

        chartData.push(row);
    });


    return chartData;
}

function minutesToHours(minutes) {
    return Number.isFinite(minutes) ? minutes / 60 : null;
}

function getIntervalDeclaredWorkingHours(endDate, userId = null, intervalType = INTERVAL_TYPE.MONTHLY) {
    if (userId !== null) {
        // These records cover whole months, even when a weekly interval ends on the same date.
        if (intervalType !== INTERVAL_TYPE.MONTHLY) {
            return null;
        }
        // Linked user IDs resolve to the same person record; combined spreadsheet hours are read once.
        const data = getDeclaredWorkForUser(userId)?.months[endDate];
        if (!data) {
            return null;
        }

        const calendarHours = minutesToHours(data.calendarMinutes);
        const qa = minutesToHours(data.qaMinutes);
        const filler = minutesToHours(data.fillerMinutes);
        return {
            calendarHours,
            taskHours: minutesToHours(data.taskMinutes),
            qa,
            filler,
            spreadsheetCellScore: data.spreadsheetCellScore,
            // Total declared working hours are QA + Filler; both must be available.
            total: qa !== null && filler !== null ? qa + filler : null,
        };
    }

    const intervalsToWorkingMinutesMap = {
        1711929599999: { filler: 113790, qa: 12780, task:0 },
        1714521599999: { filler: 145350, qa: 13410, task:0 },
        1717199999999: { filler: 135570, qa: 14520, task:0 },
        1719791999999: { filler: 128160, qa: 16680, task:0 },
        1722470399999: { filler: 165690, qa: 21120, task:0 },
        1725148799999: { filler: 152970, qa: 24960, task:0 },
        1727740799999: { filler: 131310, qa: 10800, task:0 },
        1730419199999: { filler: 175275, qa: 18900, task:0 },
        1733011199999: { filler: 140055, qa: 21180, task:0 },
        1735689599999: { filler: 109950, qa: 10905, task:0 },
        1738367999999: { filler: 130383, qa: 12837, task:0 },
        1740787199999: { filler: 117285, qa: 9855 , task:0},
        1743465599999: { filler: 109230, qa: 12240, task:0 },
        1746057599999: { filler: 108000, qa: 14385, task:0 },
        1748735999999: { filler: 92910, qa: 20805 , task:0},
        1751327999999: { filler: 106890, qa: 16215, task:0 },
        1754006399999: { filler: 125730, qa: 15225, task:0 },
        // 2026 monthly totals in minutes: Filler excludes Alex and Victoria (column DO); QA uses column DL.
        1769903999999: { filler: 131535, qa: 14850, task:0 }, // January
        1772323199999: { filler: 130518.9, qa: 14955, task:0 }, // February
        1775001599999: { filler: 146452.5, qa: 24382.5, task:0 }, // March
        1777593599999: { filler: 136987.5, qa: 25500, task:0 }, // April
        1780271999999: { filler: 111157.5, qa: 26760, task:0 }, // May
        1782863999999: { filler: 129570, qa: 22890, task:0 }, // June
        1785542399999: { filler: 123780, qa: 35595, task:0 }, // July
        1788220799999: { filler: 96825, qa: 36765, task:0 }, // August
        1790812799999: { filler: 81262.5, qa: 30615, task:0 }, // September
    }
    const data = intervalsToWorkingMinutesMap[endDate];
    if (data === undefined) {
        console.log(endDate);
        return null;
    }

    /** Divide by 60 to get the hours */
    const filler = data.filler / 60;
    const qa = data.qa / 60;
    return { filler, qa, total: filler + qa };
}
